import { Fragment, ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { ComponentType, GetPageViewModel } from '@/types/cml-api';
import InteractifyCarousel from '@/features/interactify/carousel/index';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import Banner from '../_components/banner';
import { BannerTile } from '../_components/banner-tile';
import { LandingButton } from '../_components/button';
import { CarouselBig } from '../_components/carousel-big';
import CarouselBrand from '../_components/carousel-brand/carousel-brand';
import { CarouselIcon } from '../_components/carousel-icon';
import { CarouselMedium } from '../_components/carousel-medium';
import CarouselText from '../_components/carousel-text/carousel-text';
import { Countdown } from '../_components/countdown';
import { MobileCarouselDesktopTile } from '../_components/mobile-carousel-desktop-tile';
import PersonalizedProductListExperiment from '../_components/personalized-product-list-experiment';
import ProductsList from '../_components/products-list-carousel/products-list';
import StorySkeleton from '../_components/stories/skeleton';
import SectionTitleDivider from '../_components/summer-shopping-route-landing/section-title-divider';
import StickyProgressBar from '../_components/summer-shopping-route-landing/sticky-progress-bar';
import { Testimonial } from '../_components/testimonial';
import { Text } from '../_components/text';
import TextStory from '../_components/text-story';
import { Video } from '../_components/video';
import { FIRST_LAZY_LOAD_INDEX_IN_PAGE, HOMEPAGE_VIDEO_SLUG } from '../contants';
import { LocationType } from '../type';
const Stories = dynamic(() => import('../_components/stories'), {
  ssr: false,
  loading: () => <StorySkeleton className='mr-2' />
});
export function extractHomeComponents(fetchedComponents: GetPageViewModel['components'], slug?: string): ReactNode[] {
  if (!fetchedComponents) return [];
  const isLandingPage = !!slug;
  const isSummerLanding = slug === 'summer-shopping-route';
  const extractedComponents = fetchedComponents.map((component, index) => {
    const isLazy = !PUBLIC_IS_BOT && index >= FIRST_LAZY_LOAD_INDEX_IN_PAGE;
    switch (component.type) {
      case ComponentType.CarouselBig:
        {
          return <Fragment key={component.id}>
                        <CarouselBig data={component} isLazy={isLazy} />
                        {isSummerLanding && <StickyProgressBar />}
                    </Fragment>;
        }
      case ComponentType.CarouselText:
        {
          return <CarouselText key={component.id} data={component} />;
        }
      case ComponentType.CarouselBrand:
        {
          return <CarouselBrand key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.BannerTile:
        {
          return <BannerTile key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.MobileCarouselDesktopTile:
        {
          return <MobileCarouselDesktopTile key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.CarouselMedium:
        {
          return <Fragment key={component.id}>
                        {isSummerLanding && <SectionTitleDivider title={component.title || ''} />}
                        <CarouselMedium data={component} isLazy={isLazy} index={index} isLandingPage={isLandingPage} />
                    </Fragment>;
        }
      case ComponentType.CarouselIcon:
        {
          return <CarouselIcon key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.ProductList:
        {
          return <ProductsList key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.Banner:
      case ComponentType.SmallBanner:
        {
          return <Banner key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.Story:
        {
          return <Stories key={component.id} componentId={component.id || ''} sortKey={component.sortKey || ''} />;
        }
      case ComponentType.Text:
        {
          return <Text key={component.id} text={component.text} />;
        }
      case ComponentType.Button:
        {
          return <LandingButton key={component.id} data={component} />;
        }
      case ComponentType.Video:
        {
          return <Video key={component.id} url={component.videoUrl || ''} thumbnail={component.thumbnailImageUrl || ''} />;
        }
      case ComponentType.CountDown:
        {
          return <Countdown key={component.id} data={component} />;
        }
      case ComponentType.Testimonial:
        {
          return <Testimonial key={component.id} data={component} isLazy={isLazy} />;
        }
      case ComponentType.TextStory:
        {
          return <TextStory key={component.id} data={component} />;
        }
      case ComponentType.CarouselVideo:
        {
          return <InteractifyCarousel key={component.id} slug={slug || HOMEPAGE_VIDEO_SLUG} title={component.title} className='homepage-container' componentMeta={{
            componentId: component.id || '',
            sortKey: component.sortKey || ''
          }} />;
        }
      case ComponentType.PersonalizedProductList:
        {
          return <PersonalizedProductListExperiment key={component.id} data={component} isLazy={isLazy} locationType={isLandingPage ? LocationType.LandingPage : LocationType.HomePage} />;
        }
      default:
        return <Fragment key={`component-${component.type || ''}-${index}`}></Fragment>;
    }
  });
  return extractedComponents.filter(component => component !== null);
}